import { parseUnits, readContract, writeContractAndWait } from "../viem";
import config from "../../config/config";

import MafiaHelperBotAbi from "../../abis/MafiaHelperBot.json";
import { HelperBotInfo } from "../../types/Contract/HelperBot/HelperBotInfo";
import { HelperSmuggleBotInfo } from "../../types/Contract/HelperBot/HelperSmuggleBotInfo";
import {
  parseBulletBotPlusInfo,
  parseHelperBotInfo,
  parseHelperSmuggleBotInfo,
  parseUserBotPerkInfo,
} from "../game";
import { BulletBotPlusInfo } from "../../types/Contract/HelperBot/BulletBotPlusInfo";
import { UserBotPerkInfo } from "../../types/Contract/HelperBot/UserBotPerkInfo";

const address = config.helperBotAddress;
const abi = MafiaHelperBotAbi;

export const startCrimeBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startCrimeBot",
    args: [attemptCount, perkItemIds],
    account,
  });

  return txData;
};

export const endCrimeBot = async (account: string) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endCrimeBot",
    account,
  });

  return txData;
};

export const startCarBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startCarBot",
    args: [attemptCount, perkItemIds],
    account,
  });
  return txData;
};

export const endCarBot = async (
  account: string,
  signMsg: string,
  signature: string
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endCarBot",
    args: [JSON.stringify(signMsg), signature],
    account,
  });

  console.log("Transaction successful:", txData.transactionHash);

  return txData;
};

export const startKSBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startKSBot",
    args: [attemptCount, perkItemIds],
    account,
  });

  return txData;
};

export const endKSBot = async (account: string) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endKSBot",
    account,
  });

  return txData;
};

export const startBoozeBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startBoozeBot",
    args: [attemptCount, perkItemIds],
    account,
  });
  return txData;
};

export const endBoozeBot = async (
  account: string,
  signMsg: string,
  signature: string
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endBoozeBot",
    args: [JSON.stringify(signMsg), signature],
    account,
  });
  return txData;
};

export const startNarcsBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startNarcsBot",
    args: [attemptCount, perkItemIds],
    account,
  });
  return txData;
};

export const endNarcsBot = async (
  account: string,
  signMsg: string,
  signature: string
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endNarcsBot",
    args: [JSON.stringify(signMsg), signature],
    account,
  });
  return txData;
};

export const startBulletBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startBulletBot",
    args: [attemptCount, perkItemIds],
    account,
  });
  return txData;
};

export const endBulletBot = async (
  cashAmount: string,
  account: string,
  signMsg: string,
  signature: string
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endBulletBot",
    args: [parseUnits(cashAmount, 18), JSON.stringify(signMsg), signature],
    account,
  });
  return txData;
};

export const startRaceXPBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startRacingBot",
    args: [attemptCount, perkItemIds],
    account,
  });
  return txData;
};

export const endRaceXPBot = async (account: string) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endRacingBot",
    account,
  });
  return txData;
};

export const startBustOutBot = async (
  account: string,
  attemptCount: number,
  perkItemIds: number[]
) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "startBustOutBot",
    args: [attemptCount, perkItemIds],
    account,
  });
  return txData;
};

export const endBustOutBot = async (account: string) => {
  const txData = await writeContractAndWait({
    address,
    abi,
    functionName: "endBustOutBot",
    account,
  });
  return txData;
};

/**
 * Get all helper bot info using parallel requests for better performance
 * This combines 13 separate web3 calls into parallel execution, reducing total wait time
 * @param account User account address
 * @returns All helper bot info in a single object
 */
export const getUserHelperBotInfoBatch = async (account: string) => {
  const results = await Promise.all([
    readContract({
      address,
      abi,
      functionName: "userCrimeBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userCarBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userKSBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBoozeBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userNarcsBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBulletBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBulletBotPlusInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userRacingBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBustOutBotInfo",
      args: [account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [0, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [1, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [3, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [4, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [5, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [2, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [6, account],
      account,
    }),
    readContract({
      address,
      abi,
      functionName: "userBotPerkInfo",
      args: [7, account],
      account,
    }),
  ]);

  return {
    userCrimeBotInfo: parseHelperBotInfo(results[0]) as HelperBotInfo,
    userCarBotInfo: parseHelperBotInfo(results[1]) as HelperBotInfo,
    userKSBotInfo: parseHelperBotInfo(results[2]) as HelperBotInfo,
    userBoozeBotInfo: parseHelperSmuggleBotInfo(
      results[3]
    ) as HelperSmuggleBotInfo,
    userNarcsBotInfo: parseHelperSmuggleBotInfo(
      results[4]
    ) as HelperSmuggleBotInfo,
    userBulletBotInfo: parseHelperBotInfo(results[5]) as HelperBotInfo,
    userBulletBotPlusInfo: parseBulletBotPlusInfo(
      results[6]
    ) as BulletBotPlusInfo,
    userRaceXPBotInfo: parseHelperBotInfo(results[7]) as HelperBotInfo,
    userBustOutXPBotInfo: parseHelperBotInfo(results[8]) as HelperBotInfo,
    crimeBotPerkInfo: parseUserBotPerkInfo(results[9]) as UserBotPerkInfo,
    carBotPerkInfo: parseUserBotPerkInfo(results[10]) as UserBotPerkInfo,
    boozeBotPerkInfo: parseUserBotPerkInfo(results[11]) as UserBotPerkInfo,
    narcsBotPerkInfo: parseUserBotPerkInfo(results[12]) as UserBotPerkInfo,
    bulletBotPerkInfo: parseUserBotPerkInfo(results[13]) as UserBotPerkInfo,
    killSkillBotPerkInfo: parseUserBotPerkInfo(results[14]) as UserBotPerkInfo,
    raceXPBotPerkInfo: parseUserBotPerkInfo(results[15]) as UserBotPerkInfo,
    bustOutXPBotPerkInfo: parseUserBotPerkInfo(results[16]) as UserBotPerkInfo,
  };
};
